import { useEffect, useState } from 'react';
import { api, placements as Api, type Audience, type Placement, type PlacementInput, type PlacementKind, type PubState, type Slot } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { Field, Row, StateTag } from '../components/Field';

const BLANK: PlacementInput = {
  name: '', kind: 'Broker', headline: '', subline: '', button_label: 'Open an account',
  url: '', logo_media_id: null, audience: 'All', slot: 'broker', state: 'draft', sort: 0,
};

export function Ads() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<Placement | 'new' | null>(null);
  const items = data?.items ?? [];

  const sel = useSelection(items);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Ads &amp; broker</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            Paid placements. Their links go out marked <code>rel="sponsored"</code>, which is what keeps
            them from being read as an editorial recommendation.
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New placement</button>
      </div>

      {error ? <div className="err">Could not load placements — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <BulkActions
          path="/placements" noun="placement"
          rows={sel.picked.map((p) => ({ id: p.id, label: String(p.name) }))}
          actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} />
                <th>Partner</th><th>Headline</th><th>Where</th><th>Who sees it</th>
                <th>Engagement</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={8} tick />
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="muted" style={{ padding: '26px 8px' }}>
                  No placements. With none published, the broker strip is absent from the site rather than
                  showing an invented partner’s terms.
                </td></tr>
              ) : items.map((p) => (
                <tr key={p.id} style={sel.isOn(p.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(p.id)} label={String(p.name)}
                    onChange={(v) => sel.toggle(p.id, v)} />
                  <td><div style={{ fontWeight: 700 }}>{p.name || '—'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>{p.kind}</div></td>
                  <td>{p.headline}
                    {p.subline ? <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>{p.subline}</div> : null}</td>
                  <td className="muted">{p.slot}</td>
                  <td className="muted">{p.audience}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--tx)' }}>{(p as any).clicks ?? 0} clicks</div>
                    <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{(p as any).views ?? 0} views</div>
                  </td>
                  <td><StateTag state={p.state} /></td>
                  <td className="right">
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEditing(p)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <PlacementForm
          placement={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function PlacementForm({ placement, onCancel, onSaved }: {
  placement: Placement | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<PlacementInput>(() => placement ? { ...placement } : BLANK);
  const [meta, setMeta] = useState<{ placementKinds: PlacementKind[]; audiences: Audience[]; slots: Slot[] } | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    void api.get<{ placementKinds: PlacementKind[]; audiences: Audience[]; slots: Slot[] }>('/meta')
      .then(setMeta).catch(() => {});
  }, []);

  const set = <K extends keyof PlacementInput>(k: K, v: PlacementInput[K]) => setF((p) => ({ ...p, [k]: v }));

  async function submit(state: PubState) {
    if (!f.headline.trim()) { setError('A headline is required.'); return; }
    /* A placement with no link is an advert that goes nowhere. Accept https://, http://, or internal / paths. */
    if (state === 'published' && !/^https?:\/\//i.test(f.url) && !f.url.startsWith('/')) {
      setError('A published placement needs a link starting with https://, http://, or /.');
      return;
    }
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      if (placement) await Api.update(placement.id, { ...f, state });
      else await Api.create({ ...f, state });
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={placement ? `Edit placement · ${placement.name || placement.headline}` : 'New placement'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {placement?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <Row>
        <Field label="Partner name"><input className="inp" value={f.name} autoFocus
          onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Kind">
          <select className="inp" value={f.kind} onChange={(e) => set('kind', e.target.value as PlacementKind)}>
            {(meta?.placementKinds ?? [f.kind]).map((k) => <option key={k}>{k}</option>)}
          </select></Field>
        <Field label="Slot"
          hint="broker: the partner page, and the home page unless a home card exists. home: the card on the home page only.">
          <select className="inp" value={f.slot} onChange={(e) => set('slot', e.target.value as Slot)}>
            {(meta?.slots ?? [f.slot]).map((s) => <option key={s}>{s}</option>)}
          </select></Field>
      </Row>

      <Field label="Headline"><input className="inp" value={f.headline}
        onChange={(e) => set('headline', e.target.value)} /></Field>
      <Field label="Subline"><input className="inp" value={f.subline}
        onChange={(e) => set('subline', e.target.value)} /></Field>

      <Row>
        <Field label="Button label"><input className="inp" value={f.button_label}
          onChange={(e) => set('button_label', e.target.value)} /></Field>
        <Field label="Link" hint="Sent with rel=&quot;sponsored noopener&quot;.">
          <input className="inp" value={f.url} placeholder="https://…"
            onChange={(e) => set('url', e.target.value)} /></Field>
        <Field label="Who sees it"
          hint="&quot;Not paying&quot; hides it from anyone who has bought something; &quot;Paying&quot; shows it only to them.">
          <select className="inp" value={f.audience} onChange={(e) => set('audience', e.target.value as Audience)}>
            {(meta?.audiences ?? [f.audience]).map((a) => <option key={a}>{a}</option>)}
          </select></Field>
      </Row>
    </Modal>
  );
}
